/**
 * ScrollRestore — Global scroll position manager for the SPA.
 *
 * Drop once inside <BrowserRouter>, above <Routes>. Handles all pages.
 *
 * Behavior:
 *   - PUSH / REPLACE nav (forward) → scroll window to y=0.
 *   - POP nav (browser/router back or forward) → restore the y saved
 *     for the target location.key, with a short retry loop to handle
 *     async data that grows the page height after mount.
 *
 * Storage: sessionStorage, keyed by location.key. Survives in-tab nav,
 * clears when the tab closes — correct scope.
 *
 * Window scroll only. Internal scrollable containers are out of scope.
 */
import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

const MAP_KEY = "__scroll_positions_v1";

function readMap() {
  try {
    return JSON.parse(sessionStorage.getItem(MAP_KEY) || "{}");
  } catch {
    return {};
  }
}
function writeMap(m) {
  try {
    sessionStorage.setItem(MAP_KEY, JSON.stringify(m));
  } catch {
    // storage full or blocked — silently no-op
  }
}

export default function ScrollRestore() {
  const location = useLocation();
  const navType = useNavigationType(); // "POP" | "PUSH" | "REPLACE"
  const currentKeyRef = useRef(null);

  // Keep a live ref of the current location.key so the scroll listener
  // (subscribed once) always writes to the right bucket.
  useEffect(() => {
    currentKeyRef.current = location.key;
  }, [location.key]);

  // Continuously persist scroll position for the current location.key.
  // This runs throttled via requestAnimationFrame so it's cheap.
  useEffect(() => {
    let scheduled = false;
    const save = () => {
      scheduled = false;
      const key = currentKeyRef.current;
      if (!key) return;
      const map = readMap();
      map[key] = window.scrollY;
      writeMap(map);
    };
    const onScroll = () => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(save);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    // Also save on tab hide / close.
    const saveNow = () => {
      const key = currentKeyRef.current;
      if (!key) return;
      const map = readMap();
      map[key] = window.scrollY;
      writeMap(map);
    };
    window.addEventListener("pagehide", saveNow);
    window.addEventListener("visibilitychange", saveNow);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("pagehide", saveNow);
      window.removeEventListener("visibilitychange", saveNow);
    };
  }, []);

  // Restore / reset on nav.
  useEffect(() => {
    const map = readMap();
    const target = map[location.key];

    if (navType === "POP" && typeof target === "number") {
      // Retry loop — page may still be fetching data and growing in height.
      let tries = 0;
      const attempt = () => {
        window.scrollTo(0, target);
        tries += 1;
        if (tries < 15 && Math.abs(window.scrollY - target) > 2) {
          setTimeout(attempt, 100);
        }
      };
      requestAnimationFrame(() => requestAnimationFrame(attempt));
    } else {
      // PUSH / REPLACE (or POP without saved position) → top.
      window.scrollTo(0, 0);
    }
  }, [location.key, navType]);

  return null;
}
